import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  NgZone,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { addDays, startOfDay } from 'date-fns';
import { BehaviorSubject } from 'rxjs';

export type DateRange = { from?: Date; to?: Date };

@Component({
  selector: 'app-date-ranges-input',
  templateUrl: './date-ranges-input.component.html',
  styleUrls: ['./date-ranges-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateRangesInputComponent {
  private dateRangesInput$ = new BehaviorSubject<DateRange[]>([]);
  dateRanges$ = this.dateRangesInput$.asObservable();

  @Input() set dateRanges(dateRanges: DateRange[]) {
    this.dateRangesInput$.next(dateRanges);
  }
  get dateRanges() {
    return this.dateRangesInput$.value;
  }

  @Output() dateRangesChanged = new EventEmitter<{
    dateRanges: DateRange[];
  }>();

  private showValidationInput$ = new BehaviorSubject<boolean>(false);
  showValidation$ = this.showValidationInput$.asObservable();

  @Input() set showValidation(showValidation: boolean) {
    this.showValidationInput$.next(showValidation);
  }
  get showValidation() {
    return this.showValidationInput$.value;
  }

  private isDisabledInput$ = new BehaviorSubject<boolean>(false);
  isDisabled$ = this.isDisabledInput$.asObservable();

  @Input() set isDisabled(isDisabled: boolean) {
    this.isDisabledInput$.next(isDisabled);
  }
  get isDisabled() {
    return this.isDisabledInput$.value;
  }

  // used to auto-open the last datepicker after creating it
  @ViewChildren('datePickerFrom')
  fromDatePickers?: QueryList<MatDatepicker<any>>;

  constructor(private ngZone: NgZone) {}

  resolveDateRangesChange() {
    this.dateRangesChanged.emit({ dateRanges: this.dateRanges });
  }

  addDateRangeFieldTo(range: DateRange, toDatePicker: MatDatepicker<any>) {
    range.to = startOfDay(addDays(range.from || new Date(), 1));
    this.resolveDateRangesChange();
    setTimeout(() => {
      // open after the selected date was set & applied,
      // so that the correct date is displayed in the calendar
      toDatePicker.open();
    });
  }

  removeDateRangeFieldTo(dateRange: DateRange) {
    dateRange.to = undefined;
    this.resolveDateRangesChange();
  }

  createDateRange() {
    const lastDateRange = this.dateRanges[this.dateRanges.length - 1];
    const lastDate =
      lastDateRange.to || lastDateRange.from || addDays(new Date(), 1);
    const newDate = addDays(lastDate, 1);
    const newDateRange: DateRange = { from: undefined };
    this.dateRanges.push(newDateRange);
    setTimeout(() => {
      // having this delayed fixes the calendar opening the current date instead of the new Date.
      // Apparently the setTimeout causes this thing to update the calendar on "set"
      newDateRange.from = newDate;
      setTimeout(() => {
        // open the newly created datepicker after it has been rendered & the date is set
        // removing any of these setTimeouts makes the calendar open the current date instead of the new Date
        this.fromDatePickers?.last.open();
        this.resolveDateRangesChange();
      }, 100);
    });
  }

  removeDateRangeAt(i: number) {
    if (this.dateRanges.length > 1) {
      this.dateRanges.splice(i, 1);
    }
    this.resolveDateRangesChange();
  }
}
